import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileInput } from "@/components/FileInput";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, Info } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function BudgetExcelImportDialog({ projectId, open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setLoadingPreview(false); setImporting(false); };

  const onPick = async (f) => {
    setFile(f); setPreview(null);
    if (!f) return;
    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("apply", "false");
      const { data } = await api.post(`/projects/${projectId}/simple-budget/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el Excel. Revisa que tenga columnas Concepto / Precio / Total.");
      setFile(null);
    } finally { setLoadingPreview(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("apply", "true");
      const { data } = await api.post(`/projects/${projectId}/simple-budget/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Presupuesto importado · ${data.sections} secciones, ${data.lines_count} partidas (${eur(data.total)})`);
      onImported && onImported();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el Excel.");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="budget-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Importar presupuesto desde Excel</DialogTitle>
          <DialogDescription>Sube un Excel con columnas <b>Concepto</b>, <b>Precio</b>, <b>Und</b> y <b>Total</b>. Las filas sin importes se toman como encabezados de sección.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <FileInput file={file} onFile={onPick} accept=".xlsx,.xls" testid="budget-import-file" />

          {loadingPreview && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Leyendo Excel…</div>}

          {preview && (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-sm space-y-1" data-testid="budget-import-preview">
              <div className="flex items-center gap-2 text-primary font-medium"><Info className="h-4 w-4" /> Vista previa</div>
              <div className="text-muted-foreground">Hoja: <b className="text-foreground">{preview.sheet}</b></div>
              <div className="text-muted-foreground">{(preview.sections || []).length} secciones · {preview.lines_count} partidas</div>
              <div className="text-muted-foreground">Total del presupuesto: <b className="text-foreground tabular-nums">{eur(preview.total)}</b></div>
              <div className="text-[11px] text-amber-600 pt-1">Al importar se <b>sustituye</b> el presupuesto actual del proyecto por el del Excel.</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} data-testid="budget-import-cancel">Cancelar</Button>
          <Button onClick={doImport} disabled={!preview || importing} data-testid="budget-import-confirm">
            {importing ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Importando…</> : "Importar presupuesto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
